// src/pages/inventory/TechnicianInventoryManagement.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Alert,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    MenuItem,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import {
    Add as AddIcon,
    ExpandMore as ExpandMoreIcon,
    Assignment as AssignmentIcon,
    KeyboardReturn as ReturnIcon,
    Person as PersonIcon,
    Inventory as InventoryIcon
} from '@mui/icons-material';

const TechnicianInventoryManagement = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Data states
    const [technicians, setTechnicians] = useState([]);
    const [items, setItems] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [assignments, setAssignments] = useState([]);

    // Dialog states
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [returnDialogOpen, setReturnDialogOpen] = useState(false);
    const [selectedTechnician, setSelectedTechnician] = useState('');
    const [currentAssignment, setCurrentAssignment] = useState(null);
    const [selectedItemStocks, setSelectedItemStocks] = useState([]);

    // Serialized states
    const [availableSerials, setAvailableSerials] = useState([]);
    const [selectedSerials, setSelectedSerials] = useState([]);
    const [loadingSerials, setLoadingSerials] = useState(false);
    const [assignedSerials, setAssignedSerials] = useState([]);

    // Form states
    const [assignmentForm, setAssignmentForm] = useState({
        technician_id: '',
        item_id: '',
        quantity: 1,
        warehouse_id: '',
        notes: ''
    });

    const [returnForm, setReturnForm] = useState({
        return_quantity: 1,
        condition: 'good',
        warehouse_id: '',
        notes: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const [techniciansRes, itemsRes, warehousesRes, assignmentsRes] = await Promise.all([
                fetch('/api/technicians', {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch('/api/inventory/items', {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch('/api/inventory/warehouses', {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch('/api/inventory/technicians/inventory/all', {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            if (!techniciansRes.ok || !itemsRes.ok || !warehousesRes.ok || !assignmentsRes.ok) {
                throw new Error('Error al cargar datos');
            }

            const [techniciansData, itemsData, warehousesData, assignmentsData] = await Promise.all([
                techniciansRes.json(),
                itemsRes.json(),
                warehousesRes.json(),
                assignmentsRes.json()
            ]);

            setTechnicians(techniciansData);
            setItems(itemsData);
            setWarehouses(warehousesData);
            setAssignments(assignmentsData);
            setError(null);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenAssignDialog = () => {
        setAssignmentForm({
            technician_id: '',
            item_id: '',
            quantity: 1,
            warehouse_id: '',
            notes: ''
        });
        setSelectedItemStocks([]);
        setAvailableSerials([]);
        setSelectedSerials([]);
        setAssignDialogOpen(true);
    };

    const handleItemChange = async (itemId) => {
        setAssignmentForm(prev => ({
            ...prev,
            item_id: itemId,
            warehouse_id: '' // reset warehouse selection when product changes to enforce active check
        }));
        setSelectedItemStocks([]); // clear old stock loading
        setAvailableSerials([]);
        setSelectedSerials([]);

        if (!itemId) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/inventory/items/${itemId}/stock`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const stockData = await response.json();
                setSelectedItemStocks(stockData);
            }
        } catch (error) {
            console.error('Error fetching item stocks:', error);
        }
    };

    const handleWarehouseChange = async (warehouseId) => {
        setAssignmentForm(prev => ({
            ...prev,
            warehouse_id: warehouseId
        }));
        setSelectedSerials([]);
        setAvailableSerials([]);

        if (!assignmentForm.item_id || !warehouseId) return;

        const selectedItem = items.find(i => i.id === assignmentForm.item_id);
        const isSerialized = selectedItem && selectedItem.equipment_type;

        if (isSerialized) {
            try {
                setLoadingSerials(true);
                const token = localStorage.getItem('token');
                const response = await fetch(`/api/inventory/equipment?item_id=${assignmentForm.item_id}&warehouse_id=${warehouseId}&status=in_stock`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setAvailableSerials(data);
                }
            } catch (error) {
                console.error('Error fetching available serials:', error);
            } finally {
                setLoadingSerials(false);
            }
        }
    };

    const handleAssignEquipment = async () => {
        try {
            if (!assignmentForm.technician_id || !assignmentForm.item_id || !assignmentForm.warehouse_id) {
                setError('Por favor complete todos los campos obligatorios');
                return;
            }

            const selectedItem = items.find(i => i.id === assignmentForm.item_id);
            const isSerialized = selectedItem && selectedItem.equipment_type;

            const finalQty = isSerialized ? selectedSerials.length : parseInt(assignmentForm.quantity);

            if (isNaN(finalQty) || finalQty <= 0) {
                setError(isSerialized ? 'Debe seleccionar al menos un equipo de la lista' : 'La cantidad debe ser mayor a 0');
                return;
            }

            // Validar stock específico de almacén antes de realizar la petición al servidor
            const activeStock = selectedItemStocks.find(s => s.warehouse_id === assignmentForm.warehouse_id);
            const availableQty = isSerialized ? availableSerials.length : (activeStock ? parseInt(activeStock.quantity) : 0);

            if (availableQty < finalQty) {
                setError(isSerialized
                    ? `No es posible realizar la asignación. Solo posee ${availableQty} equipos físicos disponibles.`
                    : `No es posible realizar la asignación. El almacén seleccionado solo posee ${availableQty} unidades disponibles de este producto.`
                );
                return;
            }

            setLoading(true);
            setError(null); // Limpiar errores previos
            const token = localStorage.getItem('token');

            const payload = {
                item_id: assignmentForm.item_id,
                quantity: finalQty,
                warehouse_id: assignmentForm.warehouse_id,
                notes: assignmentForm.notes.trim() || null
            };

            if (isSerialized) {
                payload.equipment_ids = selectedSerials;
            }

            const response = await fetch(
                `/api/inventory/technicians/${assignmentForm.technician_id}/inventory/assign`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Error del servidor: ${response.status}`);
            }

            const result = await response.json();

            setSuccess(`Equipo asignado exitosamente: ${result.quantity} ${result.item} a ${result.technician}`);
            setTimeout(() => setSuccess(null), 5000);

            setAssignDialogOpen(false);
            setAssignmentForm({
                technician_id: '',
                item_id: '',
                quantity: 1,
                warehouse_id: '',
                notes: ''
            });
            setSelectedSerials([]);
            setAvailableSerials([]);

            await fetchData();
        } catch (error) {
            console.error('Error assigning equipment:', error);
            setError(error.message || 'Error desconocido al asignar equipo');
        } finally {
            setLoading(false);
        }
    };

    const handleReturnEquipment = async () => {
        try {
            if (!returnForm.warehouse_id) {
                setError('Por favor seleccione un almacén');
                return;
            }

            const selectedItem = items.find(i => i.id === currentAssignment.item_id);
            const isSerialized = selectedItem && selectedItem.equipment_type;

            const finalReturnQty = isSerialized ? selectedSerials.length : parseInt(returnForm.return_quantity);

            if (isSerialized && finalReturnQty === 0) {
                setError('Por favor seleccione al menos un equipo para devolver');
                return;
            }

            setLoading(true);
            const token = localStorage.getItem('token');

            const payload = {
                return_quantity: finalReturnQty,
                condition: returnForm.condition,
                warehouse_id: returnForm.warehouse_id,
                notes: returnForm.notes
            };

            if (isSerialized) {
                payload.equipment_ids = selectedSerials;
            }

            const response = await fetch(
                `/api/inventory/technicians/${currentAssignment.technician_id}/inventory/${currentAssignment.id}/return`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al devolver equipo');
            }

            setSuccess('Equipo devuelto exitosamente');
            setTimeout(() => setSuccess(null), 5000);

            setReturnDialogOpen(false);
            setCurrentAssignment(null);
            setReturnForm({
                return_quantity: 1,
                condition: 'good',
                warehouse_id: '',
                notes: ''
            });
            setSelectedSerials([]);
            setAssignedSerials([]);

            await fetchData();
        } catch (error) {
            console.error('Error returning equipment:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const openReturnDialog = async (assignment) => {
        setCurrentAssignment(assignment);
        setReturnForm({
            return_quantity: 1,
            condition: 'good',
            warehouse_id: '',
            notes: ''
        });
        setSelectedSerials([]);
        setAssignedSerials([]);

        const selectedItem = items.find(i => i.id === assignment.item_id);
        const isSerialized = selectedItem && selectedItem.equipment_type;

        if (isSerialized) {
            try {
                setLoadingSerials(true);
                const token = localStorage.getItem('token');
                const response = await fetch(`/api/inventory/equipment?item_id=${assignment.item_id}&technician_id=${assignment.technician_id}&status=assigned_to_tech`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setAssignedSerials(data);
                }
            } catch (error) {
                console.error('Error fetching assigned serials:', error);
            } finally {
                setLoadingSerials(false);
            }
        }

        setReturnDialogOpen(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'assigned': return 'success';
            case 'returned': return 'default';
            case 'lost': return 'error';
            case 'damaged': return 'warning';
            default: return 'default';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'assigned': return 'Asignado';
            case 'returned': return 'Devuelto';
            case 'lost': return 'Perdido';
            case 'damaged': return 'Dañado';
            default: return status;
        }
    };

    // Agrupar asignaciones por técnico
    const assignmentsByTechnician = assignments.reduce((acc, assignment) => {
        const key = assignment.technician_id;
        if (!acc[key]) {
            acc[key] = {
                technician: assignment.technician_name,
                phone: assignment.technician_phone,
                assignments: []
            };
        }
        acc[key].assignments.push(assignment);
        return acc;
    }, {});

    if (loading && assignments.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Inventario de Técnicos
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenAssignDialog}
                >
                    Asignar Equipo
                </Button>
            </Box>

            {/* Lista de asignaciones agrupadas por técnico */}
            {Object.keys(assignmentsByTechnician).length > 0 ? (
                Object.entries(assignmentsByTechnician).map(([technicianId, data]) => (
                    <Accordion key={technicianId} sx={{ mb: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <PersonIcon />
                                <Typography variant="h6">{data.technician}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {data.phone}
                                </Typography>
                                <Chip
                                    label={`${data.assignments.length} equipos`}
                                    size="small"
                                    color="primary"
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Equipo</TableCell>
                                            <TableCell>Modelo/Marca</TableCell>
                                            <TableCell>Cantidad</TableCell>
                                            <TableCell>Fecha Asignación</TableCell>
                                            <TableCell>Estado</TableCell>
                                            <TableCell>Asignado por</TableCell>
                                            <TableCell align="right">Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.assignments.map((assignment) => (
                                            <TableRow key={assignment.id}>
                                                <TableCell>{assignment.item_name}</TableCell>
                                                <TableCell>
                                                    {assignment.model} - {assignment.brand}
                                                </TableCell>
                                                <TableCell>{assignment.quantity}</TableCell>
                                                <TableCell>
                                                    {new Date(assignment.assigned_date).toLocaleDateString()}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={getStatusLabel(assignment.status)}
                                                        color={getStatusColor(assignment.status)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>{assignment.assigned_by_name}</TableCell>
                                                <TableCell align="right">
                                                    {assignment.status === 'assigned' && (
                                                        <IconButton
                                                            color="warning"
                                                            size="small"
                                                            onClick={() => openReturnDialog(assignment)}
                                                            title="Devolver equipo"
                                                        >
                                                            <ReturnIcon />
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                ))
            ) : (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                        No hay equipos asignados a técnicos
                    </Typography>
                </Paper>
            )}

            {/* Dialog para asignar equipo */}
            <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Asignar Equipo a Técnico</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Técnico</InputLabel>
                                <Select
                                    value={assignmentForm.technician_id}
                                    onChange={(e) => setAssignmentForm(prev => ({
                                        ...prev,
                                        technician_id: e.target.value
                                    }))}
                                >
                                    {technicians.map((tech) => (
                                        <MenuItem key={tech.id} value={tech.id}>
                                            {tech.name} - {tech.phone}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Equipo</InputLabel>
                                <Select
                                    value={assignmentForm.item_id}
                                    onChange={(e) => handleItemChange(e.target.value)}
                                >
                                    {items.filter(item => item.total_stock > 0).map((item) => (
                                        <MenuItem key={item.id} value={item.id}>
                                            {item.name} - Stock: {item.total_stock}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <FormControl fullWidth>
                                <InputLabel>Almacén</InputLabel>
                                <Select
                                    value={assignmentForm.warehouse_id}
                                    onChange={(e) => handleWarehouseChange(e.target.value)}
                                >
                                    {warehouses.map((warehouse) => {
                                        const activeStock = selectedItemStocks.find(s => s.warehouse_id === warehouse.id);
                                        const availableQty = activeStock ? parseInt(activeStock.quantity) : 0;
                                        return (
                                            <MenuItem key={warehouse.id} value={warehouse.id}>
                                                {warehouse.name} {assignmentForm.item_id ? `(Disp: ${availableQty})` : ''}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                                {assignmentForm.item_id && assignmentForm.warehouse_id && (
                                    <Typography
                                        variant="caption"
                                        display="block"
                                        color={
                                            (() => {
                                                const selectedItem = items.find(i => i.id === assignmentForm.item_id);
                                                const isSerialized = selectedItem && selectedItem.equipment_type;
                                                if (isSerialized) {
                                                    return availableSerials.length > 0 ? "success.main" : "error.main";
                                                }
                                                const activeStock = selectedItemStocks.find(s => s.warehouse_id === assignmentForm.warehouse_id);
                                                const availableQty = activeStock ? parseInt(activeStock.quantity) : 0;
                                                return availableQty > 0 ? "success.main" : "error.main";
                                            })()
                                        }
                                        sx={{ mt: 1, ml: 1, fontWeight: 'medium' }}
                                    >
                                        Stock disponible en este almacén: {
                                            (() => {
                                                const selectedItem = items.find(i => i.id === assignmentForm.item_id);
                                                const isSerialized = selectedItem && selectedItem.equipment_type;
                                                if (isSerialized) {
                                                    return availableSerials.length;
                                                }
                                                const activeStock = selectedItemStocks.find(s => s.warehouse_id === assignmentForm.warehouse_id);
                                                return activeStock ? parseInt(activeStock.quantity) : 0;
                                            })()
                                        } unidades
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                        {(() => {
                            const selectedItem = items.find(i => i.id === assignmentForm.item_id);
                            const isSerialized = selectedItem && selectedItem.equipment_type;
                            
                            if (isSerialized) {
                                return (
                                    <Grid item xs={12}>
                                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                Seleccionar Equipos por S/N y MAC ({selectedSerials.length} seleccionados)
                                            </Typography>
                                            {loadingSerials ? (
                                                <CircularProgress size={24} sx={{ my: 1 }} />
                                            ) : availableSerials.length > 0 ? (
                                                <Grid container spacing={1}>
                                                    {availableSerials.map((eq) => {
                                                        const isChecked = selectedSerials.includes(eq.id);
                                                        return (
                                                            <Grid item xs={12} sm={6} key={eq.id}>
                                                                <Paper 
                                                                    variant="outlined" 
                                                                    sx={{ 
                                                                        p: 1, 
                                                                        cursor: 'pointer',
                                                                        borderColor: isChecked ? 'primary.main' : 'divider',
                                                                        bgcolor: isChecked ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
                                                                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                                                                    }}
                                                                    onClick={() => {
                                                                        if (isChecked) {
                                                                            setSelectedSerials(prev => prev.filter(id => id !== eq.id));
                                                                        } else {
                                                                            setSelectedSerials(prev => [...prev, eq.id]);
                                                                        }
                                                                    }}
                                                                >
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <Box>
                                                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                                                S/N: {eq.serial_number}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                MAC: {eq.mac_address} | {eq.condition === 'new' ? 'Nuevo' : eq.condition}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Chip 
                                                                            size="small" 
                                                                            label={isChecked ? "Seleccionado" : "Disponible"} 
                                                                            color={isChecked ? "primary" : "default"} 
                                                                        />
                                                                    </Box>
                                                                </Paper>
                                                            </Grid>
                                                        );
                                                    })}
                                                </Grid>
                                            ) : (
                                                <Alert severity="warning" sx={{ mt: 1 }}>
                                                    No hay equipos físicos (S/N) disponibles en stock para el almacén seleccionado. Registre una compra de series primero.
                                                </Alert>
                                            )}
                                        </Paper>
                                    </Grid>
                                );
                            } else {
                                return (
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Cantidad"
                                            value={assignmentForm.quantity}
                                            onChange={(e) => setAssignmentForm(prev => ({
                                                ...prev,
                                                quantity: e.target.value
                                            }))}
                                            inputProps={{ min: 1 }}
                                        />
                                    </Grid>
                                );
                            }
                        })()}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Notas"
                                value={assignmentForm.notes}
                                onChange={(e) => setAssignmentForm(prev => ({
                                    ...prev,
                                    notes: e.target.value
                                }))}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignDialogOpen(false)}>Cancelar</Button>
                    <Button
                        onClick={handleAssignEquipment}
                        variant="contained"
                        disabled={loading}
                    >
                        Asignar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog para devolver equipo */}
            <Dialog open={returnDialogOpen} onClose={() => setReturnDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Devolver Equipo</DialogTitle>
                <DialogContent>
                    {currentAssignment && (
                        <Box sx={{ pt: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                <strong>Equipo:</strong> {currentAssignment.item_name}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                <strong>Técnico:</strong> {currentAssignment.technician_name}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 3 }}>
                                <strong>Cantidad asignada:</strong> {currentAssignment.quantity}
                            </Typography>

                            <Grid container spacing={2}>
                                {(() => {
                                    const selectedItem = items.find(i => i.id === currentAssignment.item_id);
                                    const isSerialized = selectedItem && selectedItem.equipment_type;

                                    if (isSerialized) {
                                        return (
                                            <Grid item xs={12}>
                                                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', mb: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                        Seleccionar S/N y MAC de Equipos a Devolver ({selectedSerials.length} seleccionados)
                                                    </Typography>
                                                    {loadingSerials ? (
                                                        <CircularProgress size={24} sx={{ my: 1 }} />
                                                    ) : assignedSerials.length > 0 ? (
                                                        <Grid container spacing={1}>
                                                            {assignedSerials.map((eq) => {
                                                                const isChecked = selectedSerials.includes(eq.id);
                                                                return (
                                                                    <Grid item xs={12} sm={6} key={eq.id}>
                                                                        <Paper 
                                                                            variant="outlined" 
                                                                            sx={{ 
                                                                                p: 1, 
                                                                                cursor: 'pointer',
                                                                                borderColor: isChecked ? 'warning.main' : 'divider',
                                                                                bgcolor: isChecked ? 'rgba(237, 108, 2, 0.08)' : 'background.paper',
                                                                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                                                                            }}
                                                                            onClick={() => {
                                                                                if (isChecked) {
                                                                                    setSelectedSerials(prev => prev.filter(id => id !== eq.id));
                                                                                } else {
                                                                                    setSelectedSerials(prev => [...prev, eq.id]);
                                                                                }
                                                                            }}
                                                                        >
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                <Box>
                                                                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                                                        S/N: {eq.serial_number}
                                                                                    </Typography>
                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                        MAC: {eq.mac_address}
                                                                                    </Typography>
                                                                                </Box>
                                                                                <Chip 
                                                                                    size="small" 
                                                                                    label={isChecked ? "Seleccionado" : "Entregado"} 
                                                                                    color={isChecked ? "warning" : "default"} 
                                                                                />
                                                                            </Box>
                                                                        </Paper>
                                                                    </Grid>
                                                                );
                                                            })}
                                                        </Grid>
                                                    ) : (
                                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                                            No se encontraron equipos individuales registrados con S/N para este técnico en la base de datos.
                                                        </Alert>
                                                    )}
                                                </Paper>
                                            </Grid>
                                        );
                                    } else {
                                        return (
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    type="number"
                                                    label="Cantidad a devolver"
                                                    value={returnForm.return_quantity}
                                                    onChange={(e) => setReturnForm(prev => ({
                                                        ...prev,
                                                        return_quantity: e.target.value
                                                    }))}
                                                    inputProps={{
                                                        min: 1,
                                                        max: currentAssignment.quantity
                                                    }}
                                                />
                                            </Grid>
                                        );
                                    }
                                })()}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Condición</InputLabel>
                                        <Select
                                            value={returnForm.condition}
                                            onChange={(e) => setReturnForm(prev => ({
                                                ...prev,
                                                condition: e.target.value
                                            }))}
                                        >
                                            <MenuItem value="good">Buena</MenuItem>
                                            <MenuItem value="damaged">Dañado</MenuItem>
                                            <MenuItem value="lost">Perdido</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Almacén destino</InputLabel>
                                        <Select
                                            value={returnForm.warehouse_id}
                                            onChange={(e) => setReturnForm(prev => ({
                                                ...prev,
                                                warehouse_id: e.target.value
                                            }))}
                                        >
                                            {warehouses.map((warehouse) => (
                                                <MenuItem key={warehouse.id} value={warehouse.id}>
                                                    {warehouse.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label="Notas"
                                        value={returnForm.notes}
                                        onChange={(e) => setReturnForm(prev => ({
                                            ...prev,
                                            notes: e.target.value
                                        }))}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReturnDialogOpen(false)}>Cancelar</Button>
                    <Button
                        onClick={handleReturnEquipment}
                        variant="contained"
                        color="warning"
                        disabled={loading}
                    >
                        Devolver
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TechnicianInventoryManagement;